import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import BarcodeScanner from '../components/BarcodeScanner';

const FRUIT_EMOJIS = {
  Orange: '🍊', Lemon: '🍋', Grapefruit: '🍈', Mandarin: '🍊',
  Lime: '🍈', Tangerine: '🍊', Clementine: '🍊', Kumquat: '🟠'
};

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const [form, setForm] = useState({
    packager_number: '', product_id: '', minor_defects: '0', major_defects: '0', critical_defects: '0', notes: ''
  });
  const [photos, setPhotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [packagers, setPackagers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [packagerSearch, setPackagerSearch] = useState('');
  const [showPackagerDropdown, setShowPackagerDropdown] = useState(false);
  const packagerRef = useRef();
  const fileRef = useRef();

  const fetchPackagers = () => api.get('/admin/packagers?active_only=1').then(res => setPackagers(res.data)).catch(() => {});

  useEffect(() => {
    fetchPackagers();
    api.get('/admin/products?active_only=true').then(res => setProducts(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (packagerRef.current && !packagerRef.current.contains(e.target)) {
        setShowPackagerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProductChange = (productId) => {
    setForm({ ...form, product_id: productId });
    const product = products.find(p => p.id === parseInt(productId));
    setSelectedProduct(product || null);
  };

  const totalPieces = selectedProduct ? selectedProduct.pieces_per_box : 0;
  const minor = parseInt(form.minor_defects) || 0;
  const major = parseInt(form.major_defects) || 0;
  const critical = parseInt(form.critical_defects) || 0;

  const minorPct = totalPieces > 0 ? ((minor / totalPieces) * 100).toFixed(2) : '0.00';
  const majorPct = totalPieces > 0 ? ((major / totalPieces) * 100).toFixed(2) : '0.00';
  const criticalPct = totalPieces > 0 ? ((critical / totalPieces) * 100).toFixed(2) : '0.00';
  const totalPct = totalPieces > 0 ? (((minor + major + critical) / totalPieces) * 100).toFixed(2) : '0.00';

  // Compare against product-specific tolerances
  const minorTol = selectedProduct ? parseFloat(selectedProduct.minor_tolerance) : 10;
  const majorTol = selectedProduct ? parseFloat(selectedProduct.major_tolerance) : 5;
  const criticalTol = selectedProduct ? parseFloat(selectedProduct.critical_tolerance) : 1;

  const minorLevel = parseFloat(minorPct) > minorTol ? 'high' : parseFloat(minorPct) > minorTol * 0.5 ? 'medium' : 'low';
  const majorLevel = parseFloat(majorPct) > majorTol ? 'high' : parseFloat(majorPct) > majorTol * 0.5 ? 'medium' : 'low';
  const criticalLevel = parseFloat(criticalPct) > criticalTol ? 'high' : parseFloat(criticalPct) > 0 ? 'medium' : 'low';
  const totalLevel = parseFloat(totalPct) > (minorTol + majorTol) ? 'high' : parseFloat(totalPct) > (minorTol + majorTol) * 0.5 ? 'medium' : 'low';

  const productsByFruit = products.reduce((acc, p) => {
    if (!acc[p.fruit_type]) acc[p.fruit_type] = [];
    acc[p.fruit_type].push(p);
    return acc;
  }, {});

  const handlePhotoChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const combined = [...photos, ...newFiles].slice(0, 5);
    setPhotos(combined);
    setPreviews(combined.map(f => URL.createObjectURL(f)));
    if (fileRef.current) fileRef.current.value = '';
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
    setPreviews(previews.filter((_, i) => i !== index));
  };

  const handlePackagerScanned = (packagerNumber) => {
    const p = packagers.find(pk => pk.packager_number === packagerNumber);
    setForm({ ...form, packager_number: packagerNumber });
    setPackagerSearch(p ? `${p.packager_number} - ${p.name}` : packagerNumber);
  };

  const handlePackagerCreated = (packagerNumber) => {
    fetchPackagers().then((res) => {
      const updated = res?.data || packagers;
      const p = updated.find(pk => pk.packager_number === packagerNumber);
      setForm({ ...form, packager_number: packagerNumber });
      setPackagerSearch(p ? `${p.packager_number} - ${p.name}` : packagerNumber);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('packager_number', form.packager_number);
      formData.append('product_id', form.product_id);
      formData.append('minor_defects', form.minor_defects);
      formData.append('major_defects', form.major_defects);
      formData.append('critical_defects', form.critical_defects);
      formData.append('notes', form.notes);
      photos.forEach(p => formData.append('photos', p));

      await api.post('/inspections', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess(true);
      setForm({ packager_number: '', product_id: '', minor_defects: '0', major_defects: '0', critical_defects: '0', notes: '' });
      setSelectedProduct(null);
      setPhotos([]);
      setPreviews([]);
      setPackagerSearch('');
      if (fileRef.current) fileRef.current.value = '';

      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit inspection.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-citrus-pattern" style={{ background: 'linear-gradient(180deg, #fff7ed 0%, #f8fafc 100%)' }}>
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="text-center mb-6 fade-in">
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-800">
            {selectedProduct ? (FRUIT_EMOJIS[selectedProduct.fruit_type] || '🍊') : '🍊'} {t('box_inspection')}
          </h1>
          <p className="text-gray-500 mt-1">{t('inspector')}: <span className="font-bold text-orange-600">{user?.username}</span></p>
        </div>

        {/* Success Animation */}
        {success && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30" onClick={() => setSuccess(false)}>
            <div className="bg-white rounded-3xl p-10 text-center shadow-2xl success-pop max-w-sm mx-4">
              <div className="text-8xl mb-4">🍊</div>
              <div className="flex justify-center space-x-2 mb-4">
                {['🍋', '🍊', '🍈', '🍊', '🍋'].map((f, i) => (
                  <span key={i} className="text-3xl fruit-bounce" style={{ animationDelay: `${i * 0.1}s` }}>{f}</span>
                ))}
              </div>
              <h2 className="text-2xl font-extrabold text-green-600 mb-2">{t('box_inspected')}</h2>
              <p className="text-gray-500">{t('inspection_saved')}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm font-medium fade-in">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-xl p-5 md:p-8 border border-orange-100 fade-in">
          {/* Barcode Scanner */}
          <div className="mb-5">
            <label className="block text-sm font-bold text-gray-700 mb-2">📦 {t('packager')}</label>
            <BarcodeScanner
              packagers={packagers}
              onPackagerSelected={handlePackagerScanned}
              onPackagerCreated={handlePackagerCreated}
            />

            {/* Searchable packager selector */}
            <div className="relative mt-2" ref={packagerRef}>
              <input
                type="text"
                placeholder={t('select_packager')}
                value={packagerSearch}
                onChange={(e) => {
                  setPackagerSearch(e.target.value);
                  setShowPackagerDropdown(true);
                  if (!e.target.value) setForm({ ...form, packager_number: '' });
                }}
                onFocus={() => setShowPackagerDropdown(true)}
                className={`w-full px-4 py-4 border-2 rounded-2xl focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all bg-white text-base ${
                  form.packager_number ? 'border-green-400 bg-green-50' : 'border-orange-200'
                }`}
              />
              {packagerSearch && (
                <button
                  type="button"
                  onClick={() => { setPackagerSearch(''); setForm({ ...form, packager_number: '' }); setShowPackagerDropdown(false); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xl font-bold"
                >×</button>
              )}
              {showPackagerDropdown && (
                <ul className="absolute z-20 w-full bg-white border-2 border-orange-200 rounded-2xl shadow-xl mt-1 max-h-56 overflow-y-auto">
                  {packagers
                    .filter(p =>
                      p.packager_number.toLowerCase().includes(packagerSearch.toLowerCase()) ||
                      p.name.toLowerCase().includes(packagerSearch.toLowerCase())
                    )
                    .map(p => (
                      <li
                        key={p.id}
                        onMouseDown={() => {
                          setForm({ ...form, packager_number: p.packager_number });
                          setPackagerSearch(`${p.packager_number} - ${p.name}`);
                          setShowPackagerDropdown(false);
                        }}
                        className="px-4 py-3 cursor-pointer hover:bg-orange-50 text-sm border-b border-orange-50 last:border-0"
                      >
                        <span className="font-bold text-gray-800">{p.packager_number}</span>
                        <span className="text-gray-500 ml-2">{p.name}</span>
                      </li>
                    ))
                  }
                  {packagers.filter(p =>
                    p.packager_number.toLowerCase().includes(packagerSearch.toLowerCase()) ||
                    p.name.toLowerCase().includes(packagerSearch.toLowerCase())
                  ).length === 0 && (
                    <li className="px-4 py-3 text-sm text-gray-400 text-center">No packers found</li>
                  )}
                </ul>
              )}
              <input type="text" value={form.packager_number} required readOnly style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }} />
            </div>
            {form.packager_number && (
              <div className="mt-2 px-3 py-2 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700 font-semibold fade-in">
                ✅ {t('packager')}: {form.packager_number} - {packagers.find(p => p.packager_number === form.packager_number)?.name || ''}
              </div>
            )}
          </div>

          {/* Product Selection */}
          <div className="mb-5">
            <label className="block text-sm font-bold text-gray-700 mb-2">🍊 {t('product')}</label>
            <select
              value={form.product_id}
              onChange={(e) => handleProductChange(e.target.value)}
              className="w-full px-4 py-4 border-2 border-orange-200 rounded-2xl focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all bg-white text-base"
              required
            >
              <option value="">{t('select_product')}</option>
              {Object.entries(productsByFruit).map(([fruit, prods]) => (
                <optgroup key={fruit} label={`${FRUIT_EMOJIS[fruit] || '🍊'} ${fruit}`}>
                  {prods.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.product_code} | {t('size_code')} {p.size_code} ({p.diameter_range}) | {p.pieces_per_box} pcs | {p.quality_class}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Product Info Card */}
          {selectedProduct && (
            <div className="mb-5 p-4 bg-orange-50 rounded-2xl border border-orange-200 fade-in">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-500 font-semibold">{t('fruit')}</div>
                  <div className="font-bold text-gray-800 text-lg">{FRUIT_EMOJIS[selectedProduct.fruit_type]} {selectedProduct.fruit_type}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 font-semibold">{t('size_code')}</div>
                  <div className="font-bold text-gray-800 text-lg">{selectedProduct.size_code}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 font-semibold">{t('diameter')}</div>
                  <div className="font-bold text-gray-800">{selectedProduct.diameter_range}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 font-semibold">{t('pieces_per_box')}</div>
                  <div className="font-bold text-orange-600 text-lg">{selectedProduct.pieces_per_box}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 font-semibold">{t('quality_class')}</div>
                  <div className="font-bold text-gray-800">{selectedProduct.quality_class}</div>
                </div>
              </div>
            </div>
          )}

          {/* Minor, Major & Critical Defects */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div>
              <label className="block text-sm font-bold text-yellow-700 mb-2">⚡ {t('minor_defects')}</label>
              <input type="number" min="0" max={totalPieces || 9999} value={form.minor_defects}
                onChange={(e) => setForm({ ...form, minor_defects: e.target.value })}
                className="w-full px-3 py-4 border-2 border-yellow-300 rounded-2xl focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100 transition-all text-center text-xl font-bold"
                required />
              <p className="text-xs text-gray-400 mt-1 text-center">{t('minor_desc')}</p>
            </div>
            <div>
              <label className="block text-sm font-bold text-red-700 mb-2">🚨 {t('major_defects')}</label>
              <input type="number" min="0" max={totalPieces || 9999} value={form.major_defects}
                onChange={(e) => setForm({ ...form, major_defects: e.target.value })}
                className="w-full px-3 py-4 border-2 border-red-300 rounded-2xl focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all text-center text-xl font-bold"
                required />
              <p className="text-xs text-gray-400 mt-1 text-center">{t('major_desc')}</p>
            </div>
            <div>
              <label className="block text-sm font-bold text-purple-700 mb-2">💀 {t('critical_defects')}</label>
              <input type="number" min="0" max={totalPieces || 9999} value={form.critical_defects}
                onChange={(e) => setForm({ ...form, critical_defects: e.target.value })}
                className="w-full px-3 py-4 border-2 border-purple-400 rounded-2xl focus:outline-none focus:border-purple-600 focus:ring-2 focus:ring-purple-100 transition-all text-center text-xl font-bold bg-purple-50"
                required />
              <p className="text-xs text-gray-400 mt-1 text-center">{t('critical_desc')}</p>
            </div>
          </div>

          {/* Critical Warning Banner */}
          {critical > 0 && (
            <div className="mb-5 p-4 bg-purple-900 text-white rounded-2xl border-2 border-purple-400 fade-in">
              <div className="flex items-center gap-3">
                <span className="text-4xl">💀</span>
                <div>
                  <div className="font-extrabold text-lg">{t('critical_warning')}</div>
                  <div className="text-purple-200 text-sm">{critical} {t('critical_defects').toLowerCase()} ({criticalPct}%) - {t('critical_tolerance')}: {criticalTol}%</div>
                </div>
              </div>
            </div>
          )}

          {/* Defect Percentages */}
          <div className="grid grid-cols-4 gap-2 mb-5">
            <div className={`border-2 rounded-2xl p-3 text-center ${
              minorLevel === 'high' ? 'bg-yellow-100 border-yellow-400' : minorLevel === 'medium' ? 'bg-yellow-50 border-yellow-300' : 'bg-green-50 border-green-300'
            }`}>
              <div className="text-xs font-bold text-yellow-600">{t('minor_pct')}</div>
              <div className={`text-lg font-extrabold ${minorLevel === 'high' ? 'text-yellow-800' : 'text-yellow-700'}`}>{minorPct}%</div>
              <div className="text-xs text-gray-400">/ {minorTol}%</div>
            </div>
            <div className={`border-2 rounded-2xl p-3 text-center ${
              majorLevel === 'high' ? 'bg-red-50 border-red-300' : majorLevel === 'medium' ? 'bg-orange-50 border-orange-300' : 'bg-green-50 border-green-300'
            }`}>
              <div className="text-xs font-bold text-red-600">{t('major_pct')}</div>
              <div className={`text-lg font-extrabold ${majorLevel === 'high' ? 'text-red-700' : majorLevel === 'medium' ? 'text-orange-700' : 'text-green-700'}`}>{majorPct}%</div>
              <div className="text-xs text-gray-400">/ {majorTol}%</div>
            </div>
            <div className={`border-2 rounded-2xl p-3 text-center ${
              criticalLevel === 'high' ? 'bg-purple-100 border-purple-400' : criticalLevel === 'medium' ? 'bg-purple-50 border-purple-300' : 'bg-green-50 border-green-300'
            }`}>
              <div className="text-xs font-bold text-purple-600">{t('critical_pct')}</div>
              <div className={`text-lg font-extrabold ${criticalLevel === 'high' ? 'text-purple-800' : criticalLevel === 'medium' ? 'text-purple-600' : 'text-green-700'}`}>
                {criticalPct}% {criticalLevel === 'high' ? '💀' : ''}
              </div>
              <div className="text-xs text-gray-400">/ {criticalTol}%</div>
            </div>
            <div className={`border-2 rounded-2xl p-3 text-center ${
              totalLevel === 'high' ? 'bg-red-50 border-red-300' : totalLevel === 'medium' ? 'bg-yellow-50 border-yellow-300' : 'bg-green-50 border-green-300'
            }`}>
              <div className="text-xs font-bold text-gray-600">{t('total_pct')}</div>
              <div className={`text-lg font-extrabold ${totalLevel === 'high' ? 'text-red-700' : totalLevel === 'medium' ? 'text-yellow-700' : 'text-green-700'}`}>
                {totalPct}% <span className="text-xs ml-1">{totalLevel === 'high' ? '⚠️' : totalLevel === 'medium' ? '⚡' : '✅'}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-5">
            <label className="block text-sm font-bold text-gray-700 mb-2">📝 {t('notes')}</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-4 py-4 border-2 border-orange-200 rounded-2xl focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all text-base"
              rows="3" placeholder={t('notes_placeholder')} />
          </div>

          {/* Photo Upload */}
          <div className="mb-6">
            <label className="block text-sm font-bold text-gray-700 mb-2">📸 {t('photos')} ({t('photos_limit')})</label>
            <input ref={fileRef} type="file" accept="image/*" capture="camera" multiple onChange={handlePhotoChange}
              className="w-full px-4 py-4 border-2 border-dashed border-orange-300 rounded-2xl bg-orange-50 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-orange-500 file:text-white file:font-semibold hover:file:bg-orange-600 cursor-pointer" />
            {previews.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-3">
                {previews.map((url, i) => (
                  <div key={i} className="relative">
                    <img src={url} alt={`Preview ${i + 1}`} className="w-24 h-24 object-cover rounded-xl border-2 border-orange-200" />
                    <button type="button" onClick={() => removePhoto(i)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-8 h-8 text-sm font-bold shadow-lg flex items-center justify-center">X</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <button type="submit" disabled={submitting}
            className="w-full py-5 px-6 rounded-2xl text-white font-bold text-lg shadow-lg active:shadow-md active:scale-95 transition-all duration-200 disabled:opacity-50 touch-active"
            style={{ background: 'linear-gradient(135deg, #f97316, #84cc16)' }}>
            {submitting ? (
              <span className="flex items-center justify-center">
                <span className="fruit-spin inline-block mr-2">🍊</span> {t('submitting')}
              </span>
            ) : (
              `✅ ${t('submit_inspection')}`
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
